import type {
  DoctorSubstituteDocumentType,
  ReplaceDoctorSubstituteDocumentApiResponse,
} from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import http from '@/utils/http.service';

export type ReplaceDoctorSubstituteDocumentPayload = {
  doctorSubstituteId: string;
  type: DoctorSubstituteDocumentType;
  file: File;
};

export type ReplaceDoctorSubstituteDocumentResponse =
  ReplaceDoctorSubstituteDocumentApiResponse['data'];

const replaceDoctorSubstituteDocument = (
  payload: ReplaceDoctorSubstituteDocumentPayload,
): Promise<ReplaceDoctorSubstituteDocumentResponse> => {
  return http.doctorSubstitutes
    .replaceDocument(
      { id: payload.doctorSubstituteId, type: payload.type },
      { file: payload.file },
    )
    .then(({ data }) => data);
};

/**
 * Uploads a supporting document (licence de remplacement or attestation RCP),
 * replacing the previous one, then refreshes the substitute detail and the
 * current user's profile.
 *
 * @returns the mutation
 */
const useReplaceDoctorSubstituteDocumentMutation = (): UseMutationResult<
  ReplaceDoctorSubstituteDocumentResponse,
  Error,
  ReplaceDoctorSubstituteDocumentPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: replaceDoctorSubstituteDocument,
    onSuccess(_data, payload) {
      toast.success(t('doctor-substitute-documents.replace.success'));
      queryClient.invalidateQueries({
        queryKey: ['doctor-substitute', payload.doctorSubstituteId],
      });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    // Failures are toasted once by the global `MutationCache.onError`
    // (`http.errors.doctor_substitute_document_*`).
  });
};

export default useReplaceDoctorSubstituteDocumentMutation;
